import {
  RESEARCH_PREVIOUS_REPORT_STILL_PROCESSING,
  RESEARCH_PREVIOUS_REPORT_STILL_PROCESSING_STATUS_CODE,
  RESEARCH_ELECTRONIC_SIGNATURES_ERRORS,
} from '@/config/research';

export const ProjectResultsReports = {
  namespaced: true,

  state: {
    projectResultsReports: [],
    projectResultsReport: null,
    projectResultsReportTableData: [],
    projectResultsReportTableDataTotal: 0,
    projectResultsReportsMeta: null,
    projectResultsReportResultsViewUrl: null,
    signReportUuid: null,
    resultsVersion: null,

    isGettingProjectResultsReports: false,
    isGettingProjectResultsReport: false,
    isGettingProjectResultsReportTableData: false,
    isCreatingProjectResultsReport: false,
    isUpdatingProjectResultsReport: false,
    isDeletingProjectResultsReport: false,
    isSubmittingProjectResultsReportForApproval: false,
    isSigningProjectResultsReport: false,
    isExportingProjectResultsReportResultsView: false,

    projectResultsReportErrors: null,
    projectResultsReportEdit: null,
    projectResultsReportCopy: null,
    projectResultsReportDelete: null,

    eSignatureError: null,
  },

  mutations: {
    setProjectResultsReports(state, projectResultsReports) {
      state.projectResultsReports = projectResultsReports;
    },
    setProjectResultsReport(state, projectResultsReport) {
      state.projectResultsReport = projectResultsReport;
    },
    setProjectResultsReportTableData(state, projectResultsReportTableData) {
      state.projectResultsReportTableData = projectResultsReportTableData;
    },
    setIsGettingProjectResultsReports(state, status) {
      state.isGettingProjectResultsReports = status;
    },
    setIsGettingProjectResultsReport(state, status) {
      state.isGettingProjectResultsReport = status;
    },
    setIsGettingProjectResultsReportTableData(state, status) {
      state.isGettingProjectResultsReportTableData = status;
    },
    setIsCreatingProjectResultsReport(state, status) {
      state.isCreatingProjectResultsReport = status;
    },
    setIsUpdatingProjectResultsReport(state, status) {
      state.isUpdatingProjectResultsReport = status;
    },
    setIsDeletingProjectResultsReport(state, status) {
      state.isDeletingProjectResultsReport = status;
    },
    setProjectResultsReportErrors(state, projectResultsReportErrors) {
      state.projectResultsReportErrors = projectResultsReportErrors;
    },
    setProjectResultsReportsMeta(state, projectResultsReportsMeta) {
      state.projectResultsReportsMeta = projectResultsReportsMeta;
    },
    setProjectResultsReportEdit(state, projectResultsReportEdit) {
      state.projectResultsReportEdit = projectResultsReportEdit;
    },
    setProjectResultsReportCopy(state, projectResultsReportCopy) {
      state.projectResultsReportCopy = projectResultsReportCopy;
    },
    setProjectResultsReportDelete(state, projectResultsReportDelete) {
      state.projectResultsReportDelete = projectResultsReportDelete;
    },
    setIsSubmittingProjectResultsReportForApproval(state, status) {
      state.isSubmittingProjectResultsReportForApproval = status;
    },
    setIsSigningProjectResultsReport(state, status) {
      state.isSigningProjectResultsReport = status;
    },
    setSignReportUuid(state, reportUuid) {
      state.signReportUuid = reportUuid;
    },
    setProjectResultsReportTableDataTotal(state, total) {
      state.projectResultsReportTableDataTotal = total;
    },
    setProjectResultsReportResultsViewUrl(state, url) {
      state.projectResultsReportResultsViewUrl = url;
    },
    setIsExportingProjectResultsReportResultsView(state, status) {
      state.isExportingProjectResultsReportResultsView = status;
    },
    setResultsVersion(state, version) {
      state.resultsVersion = version;
    },
    setESignatureError(state, error) {
      state.eSignatureError = error;
    },
  },

  actions: {
    async getReports({ commit }, { project, repository, filters, page, perPage, sortBy, sortDesc }) {
      commit('setIsGettingProjectResultsReports', true);
      try {
        const response = await this._vm.$api('repositories.projects.result.reports.list', {
          version: '1.0',
          project,
          repository,
          perPage,
          filter: filters,
          page,
          sortBy,
          sortDesc,
        });
        commit('setProjectResultsReports', response.data.data);
        commit('setProjectResultsReportsMeta', response.data.meta);
        commit('setIsGettingProjectResultsReports', false);
      } catch (error) {
        console.error(error);
        commit('setIsGettingProjectResultsReports', false);
      }
    },

    async getReport({ commit }, { project, repository, reportUuid }) {
      commit('setIsGettingProjectResultsReport', true);
      commit('setProjectResultsReport', null);
      try {
        const response = await this._vm.$api('repositories.projects.result.reports.find', {
          version: '1.0',
          project,
          repository,
          report: reportUuid,
        });
        commit('setProjectResultsReport', response.data.data);
        commit('setIsGettingProjectResultsReport', false);
      } catch (error) {
        console.error(error);
        commit('setIsGettingProjectResultsReport', false);
      }
    },

    async getReportTableData(
      { commit },
      { project, repository, reportUuid, page = 1, perPage = 20, sortBy, sortDesc }
    ) {
      commit('setIsGettingProjectResultsReportTableData', true);
      commit('setProjectResultsReportTableData', []);
      try {
        const response = await this._vm.$api('repositories.projects.result.reports.data.result.table', {
          version: '1.0',
          project,
          repository,
          report: reportUuid,
          perPage,
          page,
          sortBy,
          sortDesc,
        });
        commit('setProjectResultsReportTableData', response.data?.data);
        commit('setProjectResultsReportTableDataTotal', response.data?.meta?.total);
        commit('setResultsVersion', response.data?.meta?.result);
        commit('setIsGettingProjectResultsReportTableData', false);
      } catch (error) {
        console.error(error);
        commit('setProjectResultsReportTableDataTotal', 0);
        commit('setResultsVersion', null);
        commit('setIsGettingProjectResultsReportTableData', false);
      }
    },

    async createReport({ commit }, { project, repository, data }) {
      commit('setIsCreatingProjectResultsReport', true);
      commit('setProjectResultsReportErrors', null);
      try {
        await this._vm.$api(
          'repositories.projects.result.reports.store',
          {
            version: '1.0',
            project,
            repository,
          },
          data
        );
        commit('setIsCreatingProjectResultsReport', false);
      } catch (error) {
        console.error(error);
        commit('setIsCreatingProjectResultsReport', false);
        if (!error?.message?.includes(RESEARCH_PREVIOUS_REPORT_STILL_PROCESSING_STATUS_CODE)) return;
        commit('setProjectResultsReportErrors', {
          422: RESEARCH_PREVIOUS_REPORT_STILL_PROCESSING,
        });
      }
    },

    async updateReport({ commit }, { project, repository, reportUuid, data }) {
      commit('setIsUpdatingProjectResultsReport', true);
      try {
        await this._vm.$api(
          'repositories.projects.result.reports.update',
          {
            version: '1.0',
            project,
            repository,
            report: reportUuid,
          },
          data
        );
        commit('setIsUpdatingProjectResultsReport', false);
      } catch (error) {
        console.error(error);
        commit('setIsUpdatingProjectResultsReport', false);
      }
    },

    async deleteReport({ commit }, { project, repository, reportUuid }) {
      commit('setIsDeletingProjectResultsReport', true);
      try {
        await this._vm.$api('repositories.projects.result.reports.delete', {
          version: '1.0',
          project,
          repository,
          report: reportUuid,
        });
        commit('setIsDeletingProjectResultsReport', false);
      } catch (error) {
        console.error(error);
        commit('setIsDeletingProjectResultsReport', false);
      }
    },

    async submitReportForApproval({ commit }, { project, repository, reportUuid, data }) {
      commit('setIsSubmittingProjectResultsReportForApproval', true);
      try {
        await this._vm.$api(
          'repositories.projects.result.reports.submit',
          {
            version: '1.0',
            project,
            repository,
            report: reportUuid,
          },
          {
            qc_approval_individual_uuids: data.qcApprovalsMembers,
            receiving_individual_uuids: data.receivingMembers,
          }
        );
        commit('setIsSubmittingProjectResultsReportForApproval', false);
      } catch (error) {
        console.error(error);
        commit('setIsSubmittingProjectResultsReportForApproval', false);
      }
    },

    async signReport({ commit }, { project, repository, reportUuid, data }) {
      commit('setIsSigningProjectResultsReport', true);
      commit('setESignatureError', null);
      try {
        await this._vm.$api(
          'repositories.projects.result.reports.e-sign',
          {
            version: '1.0',
            project,
            repository,
            report: reportUuid,
          },
          {
            ...data,
          }
        );

        commit('setIsSigningProjectResultsReport', false);
      } catch (error) {
        if (error.message === RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE) {
          commit('setESignatureError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE);
        } else {
          commit('setESignatureError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.GENERIC_ERROR);
        }
        console.error(error);
        commit('setIsSigningProjectResultsReport', false);
      }
    },

    async exportReportResultsView({ commit }, { project, repository, reportUuid }) {
      commit('setIsExportingProjectResultsReportResultsView', true);
      commit('setProjectResultsReportResultsViewUrl', null);
      try {
        const response = await this._vm.$api('repositories.projects.result.reports.data.result.table.export', {
          version: '1.0',
          project,
          repository,
          report: reportUuid,
        });
        commit('setProjectResultsReportResultsViewUrl', response.data.data);
        commit('setIsExportingProjectResultsReportResultsView', false);
      } catch (error) {
        console.error(error);
        commit('setIsExportingProjectResultsReportResultsView', false);
      }
    },

    resetModal({ commit }) {
      commit('setIsGettingProjectResultsReport', false);
      commit('setProjectResultsReport', null);
      commit('setProjectResultsReportErrors', null);
      commit('setProjectResultsReportEdit', null);
      commit('setProjectResultsReportCopy', null);
      commit('setProjectResultsReportDelete', null);
    },

    resetESignatureError({ commit }) {
      commit('setESignatureError', null);
    },
  },
};
